import React, { useMemo } from 'react';

import {
  Box,
  VStack,
  HStack,
  Text,
  Progress,
  SimpleGrid,
  Badge,
  Button,
} from '@chakra-ui/react';
import { FaDownload } from 'react-icons/fa';

import { useRunProgressColors } from './colors';

const RunProgressPanel = ({
  title = 'Run progress',
  steps = [],
  overallProgress = 0,
  isRunning = false,
  currentMessage = '',
  runSummary = null,
  summaryStats = [],
  hasReport = false,
  onDownloadReport,
  isDownloadingReport = false,
  embedded = false,
  showSummary = null,
}) => {
  const { textColor, backgroundCardColor, borderColor, secondaryTextColor, accentColor } =
    useRunProgressColors();

  const activeStep = useMemo(
    () => steps.find((s) => s.status === 'running') || steps.find((s) => s.status === 'error'),
    [steps],
  );

  const getStatusColor = (status) => {
    if (status === 'completed') return 'green';
    if (status === 'running') return 'blue';
    if (status === 'error') return 'red';
    return 'gray';
  };

  const defaultShowSummary =
    !isRunning && runSummary && summaryStats.some((s) => (runSummary[s.key] ?? 0) > 0);
  const showOutcomeSummary = showSummary != null ? showSummary : defaultShowSummary;

  const content = (
    <>
      <VStack align="stretch" spacing={3} mb={4}>
        <Box>
          <HStack justify="space-between" mb={2}>
            <Text fontSize="sm" fontWeight="medium" color={textColor}>
              Overall progress
            </Text>
            <HStack spacing={2}>
              {isRunning && (
                <Badge colorScheme="blue" variant="subtle">
                  Running
                </Badge>
              )}
              <Text fontSize="sm" color={secondaryTextColor} fontWeight="semibold">
                {overallProgress}%
              </Text>
            </HStack>
          </HStack>
          <Progress
            value={overallProgress}
            size="md"
            width="100%"
            colorScheme="teal"
            borderRadius="full"
            hasStripe={isRunning}
            isAnimated={isRunning}
          />
          {currentMessage && (
            <Text fontSize="sm" color={secondaryTextColor} mt={2} noOfLines={3}>
              {currentMessage}
            </Text>
          )}
          {activeStep && isRunning && (
            <Text fontSize="xs" color={accentColor} mt={1}>
              Current step: {activeStep.label}
            </Text>
          )}
        </Box>

        {showOutcomeSummary && summaryStats.length > 0 && (
          <Box
            p={4}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="md"
            bg={backgroundCardColor}
          >
            <HStack justify="space-between" mb={3} flexWrap="wrap" gap={2}>
              <Text fontSize="sm" fontWeight="semibold" color={textColor}>
                Last run outcomes
              </Text>
              {hasReport && onDownloadReport && (
                <Button
                  size="sm"
                  leftIcon={<FaDownload />}
                  variant="outline"
                  colorScheme="teal"
                  onClick={onDownloadReport}
                  isLoading={isDownloadingReport}
                >
                  Download report (CSV)
                </Button>
              )}
            </HStack>
            <SimpleGrid columns={{ base: 2, md: Math.min(summaryStats.length, 5) }} spacing={3}>
              {summaryStats.map((stat) => (
                <Box key={stat.key}>
                  <Text fontSize="xs" color={secondaryTextColor}>
                    {stat.label}
                  </Text>
                  <Text fontSize="lg" fontWeight="bold" color={stat.color || textColor}>
                    {(runSummary[stat.key] ?? 0).toLocaleString()}
                  </Text>
                </Box>
              ))}
            </SimpleGrid>
          </Box>
        )}
      </VStack>

      {steps.length > 0 && (
        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={3}>
          {steps.map((step) => (
            <Box
              key={step.id}
              p={4}
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="md"
            >
              <HStack justify="space-between" mb={2}>
                <Text fontSize="sm" fontWeight="semibold" color={textColor} noOfLines={1}>
                  {step.label}
                </Text>
                <Badge size="sm" colorScheme={getStatusColor(step.status)} variant="subtle">
                  {step.status}
                </Badge>
              </HStack>
              <Progress
                value={step.percentage ?? 0}
                size="xs"
                colorScheme={getStatusColor(step.status)}
                borderRadius="full"
                mb={2}
                hasStripe={step.status === 'running'}
                isAnimated={step.status === 'running'}
              />
              <Text fontSize="xs" color={secondaryTextColor} noOfLines={2}>
                {step.description}
              </Text>
              {step.counts?.total > 0 ? (
                <Text fontSize="xs" color={textColor} mt={2} fontWeight="medium">
                  {step.counts.succeeded.toLocaleString()} passed ·{' '}
                  {step.counts.failed.toLocaleString()} failed ·{' '}
                  {step.counts.total.toLocaleString()} in step
                </Text>
              ) : (
                step.message && (
                  <Text fontSize="xs" color={secondaryTextColor} mt={1} noOfLines={2}>
                    {step.message}
                  </Text>
                )
              )}
            </Box>
          ))}
        </SimpleGrid>
      )}
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <Box
      p={5}
      bg={backgroundCardColor}
      borderRadius="lg"
      borderWidth="1px"
      borderColor={borderColor}
      boxShadow="sm"
    >
      <Text color={textColor} fontSize="md" fontWeight="semibold" mb={4}>
        {title}
      </Text>
      {content}
    </Box>
  );
};

export default RunProgressPanel;
